import React, { useEffect, useMemo, useRef, useState } from 'react';
import FlagImg from '../../../shared/assets/images/The Transport/Flag.png';
import BoatImg from '../../../shared/assets/images/The Transport/Boat.png';
import RocketImg from '../../../shared/assets/images/The Transport/Rocket.png';
import BiddyBootImg from '../../../shared/assets/images/The Transport/BiddyBoot.png';
import SkiskiImg from '../../../shared/assets/images/The Transport/Skiski.png';
import '../styles/MissionTransportProgress.css';

interface MissionTransportProgressProps {
  /** Initial duration in seconds for the timer & full journey */
  initialDurationSeconds?: number;
  /** Optional list of student muses to display riding the boat */
  boatMuses?: { id: string; name?: string; img?: string }[];
  /** Game name for display */
  gameName?: string;
  /** Number of students who completed daily practice */
  completedCount?: number;
  /** Total number of students in attendance */
  totalCount?: number;
  /** When set, triggers a musecash celebration for matching muse IDs */
  museCashCelebrationByStudentId?: Record<string, number>;
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const hashString = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h;
};

const formatSeconds = (totalSeconds: number): string => {
  const clamped = Math.max(0, Math.round(totalSeconds));
  const minutes = Math.floor(clamped / 60);
  const seconds = clamped % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const parseMmSs = (value: string, fallbackSeconds: number): number => {
  const trimmed = value.trim();
  if (!trimmed) return fallbackSeconds;
  const parts = trimmed.split(':');
  if (parts.length === 1) {
    const n = Number(parts[0]);
    return Number.isFinite(n) && n > 0 ? n : fallbackSeconds;
  }
  const [mm, ss] = parts;
  const mNum = Number(mm);
  const sNum = Number(ss);
  if (!Number.isFinite(mNum) || !Number.isFinite(sNum)) return fallbackSeconds;
  const total = mNum * 60 + sNum;
  return total > 0 ? total : fallbackSeconds;
};

const TRANSPORTS = [
  { key: 'boat', label: 'Boat', src: BoatImg },
  { key: 'rocket', label: 'Rocket', src: RocketImg },
  { key: 'boot', label: 'Biddy Boot', src: BiddyBootImg },
  { key: 'ski', label: 'Skiski', src: SkiskiImg },
] as const;

const MissionTransportProgress: React.FC<MissionTransportProgressProps> = ({
  initialDurationSeconds = 600, // default 10 minutes
  boatMuses = [],
  gameName,
  completedCount = 0,
  totalCount = 0,
  museCashCelebrationByStudentId = {},
}) => {
  const [totalSeconds, setTotalSeconds] = useState(initialDurationSeconds);
  const [remainingSeconds, setRemainingSeconds] = useState(initialDurationSeconds);
  const [isRunning, setIsRunning] = useState(false);
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState(formatSeconds(initialDurationSeconds));
  const lastTickRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const [focusedMuseId, setFocusedMuseId] = useState<string | null>(null);
  const [focusedMuseNonce, setFocusedMuseNonce] = useState<number>(0);
  const tickAudioRef = useRef<HTMLAudioElement | null>(null);
  const vehicleRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{
    museId: string;
    pointerId: number;
    targetEl: HTMLElement;
    startClientX: number;
    startClientY: number;
    startLeftPct: number;
    startTopPct: number;
  } | null>(null);
  const [activeDragMuseId, setActiveDragMuseId] = useState<string | null>(null);

  const [musePositions, setMusePositions] = useState<Record<string, { leftPct: number; topPct: number; sizePx: number }>>({});
  const [transportIndex] = useState(() => Math.floor(Math.random() * TRANSPORTS.length));

  useEffect(() => {
    // Preload tick sound once for responsive playback
    const audio = new Audio('/audio/tick-key.mp3');
    audio.preload = 'auto';
    audio.volume = 0.65;
    tickAudioRef.current = audio;
    return () => {
      tickAudioRef.current = null;
    };
  }, []);

  const progress = useMemo(() => {
    if (totalSeconds <= 0) return 0;
    const completed = totalSeconds - remainingSeconds;
    return clamp(completed / totalSeconds, 0, 1);
  }, [totalSeconds, remainingSeconds]);

  // Drive timer using requestAnimationFrame for smooth transport motion
  useEffect(() => {
    if (!isRunning) {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      lastTickRef.current = null;
      return;
    }

    const tick = (timestamp: number) => {
      if (lastTickRef.current == null) {
        lastTickRef.current = timestamp;
      }
      const deltaMs = timestamp - lastTickRef.current;
      lastTickRef.current = timestamp;
      if (deltaMs > 0) {
        setRemainingSeconds(prev => {
          const next = prev - deltaMs / 1000;
          if (next <= 0) {
            // Arrived at end
            setIsRunning(false);
            return 0;
          }
          return next;
        });
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isRunning]);

  const handlePlayPause = () => {
    setIsRunning(prev => !prev && remainingSeconds > 0);
  };

  const handleStop = () => {
    setIsRunning(false);
    setRemainingSeconds(totalSeconds);
  };

  const handleTimerClick = () => {
    setEditing(true);
    setInputValue(formatSeconds(remainingSeconds || totalSeconds));
  };

  const commitTimerEdit = () => {
    const nextTotal = parseMmSs(inputValue, totalSeconds || initialDurationSeconds);
    setTotalSeconds(nextTotal);
    setRemainingSeconds(nextTotal);
    setIsRunning(false);
    setEditing(false);
  };

  const handleTimerKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      commitTimerEdit();
    } else if (e.key === 'Escape') {
      setEditing(false);
      setInputValue(formatSeconds(remainingSeconds || totalSeconds));
    }
  };

  const transportLeftPercent = 0.02 + progress * 0.96; // keep some padding

  const getInitialPlacement = (museId: string, index: number) => {
    const seed = hashString(`${museId}:${index}`);
    const leftPct = 18 + ((seed % 1000) / 1000) * 64; // 18% -> 82%
    const topPct = 50 + (((seed >>> 10) % 1000) / 1000) * 10 - 5; // 45% -> 55%
    const sizePx = 44 + (seed % 9); // 44-52px
    return { leftPct, topPct, sizePx };
  };

  // Initialize (and preserve) draggable positions for muses
  useEffect(() => {
    if (!boatMuses || boatMuses.length === 0) {
      setMusePositions({});
      return;
    }
    setMusePositions(prev => {
      const next: Record<string, { leftPct: number; topPct: number; sizePx: number }> = { ...prev };
      // Add/update entries for current muses
      boatMuses.forEach((m, idx) => {
        if (!next[m.id]) {
          next[m.id] = getInitialPlacement(m.id, idx);
        } else {
          // Keep current position, but allow slight size refresh if desired later
          next[m.id] = { ...next[m.id], sizePx: next[m.id].sizePx || getInitialPlacement(m.id, idx).sizePx };
        }
      });
      // Remove entries for muses no longer present
      Object.keys(next).forEach(id => {
        if (!boatMuses.some(m => m.id === id)) delete next[id];
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boatMuses.map(m => m.id).join('|')]);

  const focusMuse = (id: string) => {
    setFocusedMuseId(id);
    // Force re-mount of the focused muse element so CSS animation reliably re-triggers on repeated clicks.
    setFocusedMuseNonce(Date.now());
    // Play tick key sound on wiggle/pulse trigger
    const a = tickAudioRef.current;
    if (a) {
      try {
        // Randomize pitch slightly by varying playbackRate.
        // Disable pitch preservation so rate changes affect pitch.
        (a as any).preservesPitch = false;
        (a as any).mozPreservesPitch = false;
        (a as any).webkitPreservesPitch = false;
        a.playbackRate = 0.92 + Math.random() * 0.16; // 0.92x - 1.08x
        a.currentTime = 0;
        const p = a.play();
        if (p && typeof (p as any).catch === 'function') {
          (p as any).catch(() => {});
        }
      } catch {
        // ignore playback errors (autoplay restrictions etc.)
      }
    }
  };

  const onMusePointerDown = (e: React.PointerEvent, museId: string) => {
    e.stopPropagation();
    e.preventDefault();
    const el = e.currentTarget as HTMLElement;
    el.setPointerCapture(e.pointerId);

    const pos = musePositions[museId] || getInitialPlacement(museId, 0);
    dragRef.current = {
      museId,
      pointerId: e.pointerId,
      targetEl: el,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startLeftPct: pos.leftPct,
      startTopPct: pos.topPct,
    };
    // Bring to front immediately
    setFocusedMuseId(museId);
    setActiveDragMuseId(museId);
  };

  // Window-level listeners make dragging consistent even if the pointer leaves the element.
  useEffect(() => {
    if (!activeDragMuseId) return;

    const handleMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d || d.museId !== activeDragMuseId) return;
      const rect = vehicleRef.current?.getBoundingClientRect();
      if (!rect || rect.width <= 0 || rect.height <= 0) return;

      const dx = e.clientX - d.startClientX;
      const dy = e.clientY - d.startClientY;

      const deltaLeftPct = (dx / rect.width) * 100;
      const deltaTopPct = (dy / rect.height) * 100;

      const nextLeft = clamp(d.startLeftPct + deltaLeftPct, 6, 94);
      const nextTop = clamp(d.startTopPct + deltaTopPct, 18, 92);

      setMusePositions(prev => {
        const current = prev[d.museId];
        if (!current) return prev;
        if (Math.abs(current.leftPct - nextLeft) < 0.005 && Math.abs(current.topPct - nextTop) < 0.005) return prev;
        return { ...prev, [d.museId]: { ...current, leftPct: nextLeft, topPct: nextTop } };
      });
    };

    const handleUp = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d || d.museId !== activeDragMuseId) return;
      dragRef.current = null;
      setActiveDragMuseId(null);
      try {
        d.targetEl.releasePointerCapture(d.pointerId);
      } catch {
        // ignore
      }
      // Wiggle/pulse only on release (always), not on press
      focusMuse(d.museId);
    };

    window.addEventListener('pointermove', handleMove, { passive: true });
    window.addEventListener('pointerup', handleUp, { passive: true });
    window.addEventListener('pointercancel', handleUp, { passive: true });
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
  }, [activeDragMuseId]);

  return (
    <div className="mission-transport-root">
      <div className="mission-transport-header-row">
        <button
          type="button"
          className="mission-transport-control play-pause"
          onClick={handlePlayPause}
          aria-label={isRunning ? 'Pause timer' : 'Start timer'}
        >
          {isRunning ? '⏸' : 'Start'}
        </button>
        <button
          type="button"
          className="mission-transport-control stop"
          onClick={handleStop}
          aria-label="Reset timer"
        >
          ⏹
        </button>
        <div className="mission-transport-timer" onClick={!editing ? handleTimerClick : undefined}>
          {editing ? (
            <input
              autoFocus
              className="mission-transport-timer-input"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onBlur={commitTimerEdit}
              onKeyDown={handleTimerKeyDown}
            />
          ) : (
            <span>{formatSeconds(remainingSeconds)}</span>
          )}
        </div>
      </div>

      <div className="mission-transport-track-area">
        {/* Flag at the start node */}
        <div className="mission-transport-flag-wrapper">
          <img src={FlagImg} alt="Start flag" className="mission-transport-flag" />
        </div>

        {/* Track line */}
        <div className="mission-transport-track">
          <div className="mission-transport-track-line mission-transport-track-line-base" />
          <div
            className="mission-transport-track-line mission-transport-track-line-progress"
            style={{ width: `${transportLeftPercent * 100}%` }}
          />

          {/* Nodes */}
          <div className="mission-transport-node mission-transport-node-start">
            {!isRunning && gameName && (
              <div className="mission-transport-node-label">
                {completedCount === totalCount && totalCount > 0
                  ? 'All Aboard!'
                  : `${completedCount}/${totalCount} Opened ${gameName}`}
              </div>
            )}
          </div>
          <div className="mission-transport-node mission-transport-node-mid" />
          <div className="mission-transport-node mission-transport-node-end" />

          {/* Transport */}
          <div
            className="mission-transport-vehicle"
            style={{ left: `${transportLeftPercent * 100}%` }}
            ref={vehicleRef}
          >
            <img
              src={TRANSPORTS[transportIndex]?.src || BoatImg}
              alt={`${TRANSPORTS[transportIndex]?.label || 'Boat'} transport`}
              className="mission-transport-vehicle-img"
            />
            {boatMuses.length > 0 && (
              <div className="mission-transport-muses-layer" aria-hidden="true">
                {boatMuses.map((muse, idx) => {
                  const placement = musePositions[muse.id] || getInitialPlacement(muse.id, idx);
                  if (!placement) return null;
                  const isFocused = muse.id === focusedMuseId;
                  const celebrationKey = museCashCelebrationByStudentId[muse.id] || 0;
                  const isMuseCashCelebrating = celebrationKey > 0;
                  const renderKey = `${muse.id}-${isFocused ? focusedMuseNonce : 'x'}-${celebrationKey}`;

                  if (!muse.img) {
                    return (
                      <div
                        key={renderKey}
                        className={`mission-transport-muse-wrap ${isFocused ? 'is-focused' : ''} ${isMuseCashCelebrating ? 'is-musecash' : ''}`}
                        style={{
                          left: `${placement.leftPct}%`,
                          top: `${placement.topPct}%`,
                          transform: 'translate(-50%, -100%)',
                          zIndex: isFocused ? 10 : 2,
                        }}
                        title={muse.name || 'Student'}
                        role="button"
                        tabIndex={0}
                        onPointerDown={(e) => onMusePointerDown(e, muse.id)}
                        onPointerUp={(e) => {
                          // let window listener handle release; this is only a fallback
                          e.stopPropagation();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            focusMuse(muse.id);
                          }
                        }}
                      >
                        {isMuseCashCelebrating && (
                          <div className="mission-transport-muse-cash-pop" aria-hidden="true">
                            💰
                          </div>
                        )}
                        <div className="mission-transport-muse-emoji">🎵</div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={renderKey}
                      className={`mission-transport-muse-wrap ${isFocused ? 'is-focused' : ''} ${isMuseCashCelebrating ? 'is-musecash' : ''}`}
                      style={{
                        left: `${placement.leftPct}%`,
                        top: `${placement.topPct}%`,
                        transform: 'translate(-50%, -100%)',
                        zIndex: isFocused ? 10 : 2,
                      }}
                      title={muse.name || 'Student'}
                      onPointerDown={(e) => onMusePointerDown(e, muse.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          focusMuse(muse.id);
                        }
                      }}
                    >
                      {isMuseCashCelebrating && (
                        <div className="mission-transport-muse-cash-pop" aria-hidden="true">
                          💰
                        </div>
                      )}
                      <img
                        src={muse.img}
                        alt={muse.name || 'Student muse'}
                        className="mission-transport-muse-img"
                        style={{ height: `${placement.sizePx}px` }}
                        draggable={false}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissionTransportProgress;

